/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 26 2024
 **/

import ReactQuill from "@rsupport/react-quill";
import has from "lodash-es/has";
import { Delta, Op } from "quill/core";

type ListBufferType = { type: ChatServices.Format2.Elements.List["marker"]; elements: any[] };
type BlockQuoteBufferType = { elements: any[] };
type CodeBlockBufferType = { code: Op["insert"][] };

export default class ChatParser {
	constructor(private _editor: ReactQuill) {}

	public get editor() {
		return this._editor;
	}

	public richtext(): ChatServices.Format2.Container.Richtext {
		const editor = this.editor.getEditor();
		const length = editor.getLength();
		length && editor.deleteText(length - 1, 1, "silent");

		const contents = editor.getContents();
		// console.group("Lines", contents);
		const richText: ChatServices.Format2.Container.Richtext = {
			type: "richtext",
			elements: []
		};
		const listBuffer: ListBufferType = {
			type: "unknown",
			elements: []
		};
		const blockQuoteBuffer: BlockQuoteBufferType = { elements: [] };
		const codeBlockBuffer: CodeBlockBufferType = { code: [] };
		contents.eachLine((line, attributes, index) => {
			if (!line.ops) {
				return;
			}
			// console.debug(`#Line:${index}# `, line, attributes);

			if (has(attributes, "code-block")) {
				this.add_list_buffer(richText, listBuffer);
				this.add_blockquote_buffer(richText, blockQuoteBuffer);
				line.ops.forEach((code) => codeBlockBuffer.code.push(code.insert));
			} else if (has(attributes, "blockquote")) {
				this.add_code_block_buffer(richText, codeBlockBuffer);
				this.add_list_buffer(richText, listBuffer);
				this.add_text_line(blockQuoteBuffer.elements, this.parse_lines(line.ops));
			} else if (has(attributes, "list")) {
				this.add_code_block_buffer(richText, codeBlockBuffer);
				if (listBuffer.type === "unknown") {
					listBuffer.type = attributes.list as ChatServices.Format2.Elements.List["marker"];
				}
				// 이전에 설정된 maker와 다를 경우 기존 list를 추가하고 새로 시작함
				if (listBuffer.type !== attributes.list) {
					this.add_list_buffer(richText, listBuffer);
					listBuffer.type = attributes.list as ChatServices.Format2.Elements.List["marker"];
				}
				this.add_blockquote_buffer(richText, blockQuoteBuffer);
				this.add_text_line(listBuffer.elements, this.parse_lines(line.ops));
			} else {
				this.add_code_block_buffer(richText, codeBlockBuffer);
				this.add_list_buffer(richText, listBuffer);
				this.add_blockquote_buffer(richText, blockQuoteBuffer);
				this.add_text_line(richText.elements, this.parse_lines(line.ops));
			}
		});
		// 입력된 내용 마지막에 code_block, list, blockquote 가 있을 경우 여기서 추가함
		this.add_code_block_buffer(richText, codeBlockBuffer);
		this.add_list_buffer(richText, listBuffer);
		this.add_blockquote_buffer(richText, blockQuoteBuffer);
		console.groupEnd();
		return richText;
	}

	public parse_to_delta(formatted: ChatServices.Format2.Container.Richtext) {
		let ops: Op[] = [];
		formatted.elements?.forEach((element) => {
			switch (element.type) {
				case "section": {
					ops = ops.concat(this.parse_section(element));
					break;
				}

				case "text": {
					ops.push(this.parse_text(element, true));
					break;
				}

				case "link": {
					ops.push(this.parse_link(element));
					break;
				}

				case "makeList": {
					const result = this.parse_list(element);
					if (result) {
						ops = ops.concat(result);
					}
					break;
				}

				case "codeBlock": {
					ops = ops.concat(...this.parse_code_block(element));
					break;
				}

				case "blockquote": {
					const result = this.parse_blockquote(element);
					if (result) {
						ops = ops.concat(result);
					}
					break;
				}
			}
		});
		this._editor.getEditor().setContents(ops);
	}

	private parse_text(element: ChatServices.Format2.Elements.Text, lineFeed: boolean) {
		return {
			insert: `${element.text}${lineFeed ? "\n" : ""}`,
			...(element.style ? { attributes: element.style } : {})
		};
	}

	private parse_link(element: ChatServices.Format2.Elements.Link) {
		return {
			insert: element.title,
			attributes: { link: element.url }
		};
	}

	private parse_user(element: ChatServices.Format2.Elements.User) {
		return {
			insert: {
				mention: {
					denotationChar: "@",
					id: element.id,
					value: element.name
				}
			}
		};
	}

	private parse_list(element: ChatServices.Format2.Elements.List) {
		return element.elements.reduce<Op[]>((total, el) => {
			let item: Op[] = [];
			switch (el.type) {
				case "section": {
					item = item.concat(this.parse_section(el, false));
					break;
				}

				case "text": {
					item.push(this.parse_text(el, false));
					break;
				}

				case "link": {
					item.push(this.parse_link(el));
					break;
				}

				default: {
					return total;
				}
			}

			total = total.concat([
				...item,
				{
					insert: "\n",
					attributes: { list: element.marker }
				}
			]);
			return total;
		}, []);
	}

	private parse_code_block(element: ChatServices.Format2.Elements.CodeBlock) {
		return element.code.split("\n").map((code) => [
			{
				insert: code
			},
			{
				insert: "\n",
				attributes: { "code-block": true }
			}
		]);
	}

	private parse_blockquote(element: ChatServices.Format2.Elements.BlockQuote) {
		return element.elements.reduce<Op[]>((total, el) => {
			let item: Op[] = [];
			switch (el.type) {
				case "section": {
					item = item.concat(this.parse_section(el, false));
					break;
				}

				case "text": {
					item.push(this.parse_text(el, false));
					break;
				}

				default: {
					return total;
				}
			}

			total = total.concat([
				...item,
				{
					insert: "\n",
					attributes: { blockquote: true }
				}
			]);
			return total;
		}, []);
	}

	private parse_section({ elements }: ChatServices.Format2.Elements.Section, lineFeed = true) {
		const result: Op[] = [];
		elements.forEach((element) => {
			switch (element.type) {
				case "text": {
					result.push(this.parse_text(element, false));
					break;
				}

				case "link": {
					result.push(this.parse_link(element));
					break;
				}

				case "user": {
					result.push(this.parse_user(element));
					break;
				}
			}
		});
		lineFeed && result.push({ insert: "\n" });
		return result;
	}

	private add_text_line(target: any, elements: any[]) {
		if (elements.length === 1) {
			// @ts-ignore 종류를 정하지 못했기 때문에 어쩔 수 없이 ignore 처리함
			target.push(elements[0]);
		} else {
			elements = elements.map((element) => {
				try {
					if (typeof element.text !== "string") {
						if ("mention" in element.text) {
							const { mention } = element.text as {
								mention: { denotationChar: string; id: string; index: number; value: string };
							};
							return { type: "user", id: mention.id, name: mention.value };
						}
					} else {
						return element;
					}
				} catch {
					return element;
				}
			});
			target.push({
				type: "section",
				elements
			});
		}
	}

	private add_list_buffer(richText: ChatServices.Format2.Container.Richtext, buffer: ListBufferType) {
		if (buffer.type !== "unknown") {
			richText.elements.push({
				type: "makeList",
				marker: buffer.type,
				elements: buffer.elements
			});
			buffer.type = "unknown";
			buffer.elements = [];
		}
	}

	private add_blockquote_buffer(richText: ChatServices.Format2.Container.Richtext, buffer: BlockQuoteBufferType) {
		if (buffer.elements.length > 0) {
			richText.elements.push({
				type: "blockquote",
				elements: buffer.elements
			});
			buffer.elements = [];
		}
	}

	private add_code_block_buffer(richText: ChatServices.Format2.Container.Richtext, buffer: CodeBlockBufferType) {
		if (buffer.code.length > 0) {
			richText.elements.push({
				type: "codeBlock",
				code: buffer.code.join("\n")
			});
			buffer.code = [];
		}
	}

	private parse_lines(line: any) {
		const elements: any[] = [];
		line.forEach((value: Op) => {
			elements.push(
				(value.attributes?.link && {
					type: "link",
					url: value.attributes.link,
					title: value.insert
				}) || {
					type: "text",
					text: value.insert,
					...((value.attributes && {
						style: {
							...((value.attributes.bold && { bold: true }) || {}),
							...((value.attributes.italic && { italic: true }) || {}),
							...((value.attributes.underline && { underline: true }) || {}),
							...((value.attributes.strike && { strike: true }) || {}),
							...((value.attributes.code && { code: true }) || {})
						}
					}) ||
						{})
				}
			);
		});
		if (elements.length === 0) {
			elements.push({ type: "text", text: "" });
		}
		return elements;
	}
}
